import React, { useCallback } from 'react'
import PropTypes from 'prop-types'
import Select from '../../ui/Select/Select'
import StatusDot from '../IssueStatuses/StatusDot'
import { CATEGORIES } from '../IssueStatuses/constants'
import StatusCategoryHeading from './StatusCategoryHeading'
import StatusSelectIndicator from './StatusSelectIndicator'

const DEFAULT_PLACEHOLDER = 'Sélectionner...'
const GROUPED_COMPONENTS = { GroupHeading: StatusCategoryHeading, DropdownIndicator: StatusSelectIndicator }
const GROUPED_MENU_HEIGHT = 335
const GROUPED_BORDER_COLOR = '#d1d5db'
const GROUPED_PLACEHOLDER_COLOR = '#4b5563'

const optionClassName = ({ isFocused }) => 'tw:px-3! tw:py-2! tw:rounded! tw:text-[#0a0a0a]! '
  + 'tw:leading-[19.25px]! tw:tracking-[-0.15px] tw:cursor-pointer! '
  + `${isFocused ? 'tw:bg-gray-100!' : 'tw:bg-white!'}`

const GROUPED_CLASS_NAMES = {
  menu: () => 'tw:mt-1! tw:p-2! tw:rounded! tw:shadow-[0_0_7px_0_rgba(0,0,0,0.1)]!',
  menuList: () => 'tw:p-0! tw:flex! tw:flex-col tw:gap-1 '
    + 'tw:[scrollbar-width:thin] tw:[scrollbar-color:#9ca3af_transparent]',
  group: () => 'tw:p-0! tw:flex! tw:flex-col tw:gap-1 '
    + 'tw:[&>div:last-child]:flex tw:[&>div:last-child]:flex-col tw:[&>div:last-child]:gap-1',
  option: optionClassName
}

const groupedProps = grouped => {
  if (!grouped)
    return {}
  return {
    classNames: GROUPED_CLASS_NAMES,
    controlBorderColor: GROUPED_BORDER_COLOR,
    controlPlaceholderColor: GROUPED_PLACEHOLDER_COLOR,
    maxMenuHeight: GROUPED_MENU_HEIGHT
  }
}

const renderStatusOption = option => <StatusDot color={option.color} label={option.label} />

const groupByCategory = options => CATEGORIES
  .map(category => ({
    value: category.value,
    label: category.label,
    options: options.filter(option => option.category === category.value)
  }))
  .filter(group => group.options.length > 0)

const StatusSelect = ({
  inputId, issueStatuses, value = null, onChange, grouped = false, placeholder = DEFAULT_PLACEHOLDER
}) => {
  const options = issueStatuses.map(status => ({
    value: status.id, label: status.name, color: status.color, category: status.category
  }))
  const selected = options.find(option => option.value === value) || null

  const handleChange = useCallback(option => onChange(option?.value ?? null), [onChange])

  return (
    <Select
      components={grouped ? GROUPED_COMPONENTS : null}
      formatOptionLabel={renderStatusOption}
      inputId={inputId}
      isSearchable={false}
      menuPortalTarget={document.body}
      menuPosition="fixed"
      options={grouped ? groupByCategory(options) : options}
      placeholder={placeholder}
      value={selected}
      onChange={handleChange}
      {...groupedProps(grouped)}
    />
  )
}

StatusSelect.propTypes = {
  grouped: PropTypes.bool,
  inputId: PropTypes.string,
  issueStatuses: PropTypes.arrayOf(PropTypes.shape({
    category: PropTypes.string,
    color: PropTypes.string,
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired
  })),
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  value: PropTypes.number
}

StatusSelect.defaultProps = {
  grouped: false,
  inputId: null,
  issueStatuses: [],
  placeholder: DEFAULT_PLACEHOLDER,
  value: null
}

export default StatusSelect
